import { uiSurface } from "shared/ui/uiSurface"
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { FC, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  ReportEntry,
  ReportParameters,
  UniversalReport,
} from "../../apis/appApi"

const DELIVERED_EVENTS = ["CUSTOMER_DELIVERY", "PROVIDER_DELIVERY", "PICKUP"]
const FAILED_EVENTS = ["UNABLE_TO_COMPLETE_DELIVERY", "UNABLE_TO_PICKUP"]

type TabProps = {
  entries: ReportEntry[]
  report: UniversalReport
  parameters: ReportParameters
}

export const TravelTab: FC<TabProps> = ({ entries, report, parameters }) => {
  const { t } = useTranslation()
  const [showAllTrips, setShowAllTrips] = useState(false)
  const [minDistance, setMinDistance] = useState<string>("")
  const [maxDistance, setMaxDistance] = useState<string>("")

  // Trips = entries with a courier assigned
  const trips = useMemo(() => {
    return entries.filter((e) => {
      if (!e.courier) return false
      if (!showAllTrips) {
        return e.traceability && DELIVERED_EVENTS.includes(e.traceability.event)
      }
      return (
        e.traceability &&
        [...DELIVERED_EVENTS, ...FAILED_EVENTS].includes(e.traceability.event)
      )
    })
  }, [entries, showAllTrips])

  // Failed attempts count
  const failedAttempts = useMemo(
    () =>
      entries.filter(
        (e) => e.traceability && FAILED_EVENTS.includes(e.traceability.event),
      ).length,
    [entries],
  )

  // Distance analysis
  const distanceAnalysis = useMemo(() => {
    const min = parseFloat(minDistance)
    const max = parseFloat(maxDistance)
    if (isNaN(min) || isNaN(max) || min < 0 || max < min) return null

    let nbOrderWithDistanceExtra = 0
    let totalDistanceExtra = 0
    let nbOrderWithDistanceExtraByModulo = 0
    const processedIds = new Set<number>()

    for (const entry of trips) {
      if (!entry.distance || processedIds.has(entry.order.id)) continue
      processedIds.add(entry.order.id)
      if (entry.distance.value >= min && entry.distance.value <= max) {
        nbOrderWithDistanceExtra++
        totalDistanceExtra += entry.distance.value
        if (min > 0) {
          nbOrderWithDistanceExtraByModulo += Math.floor(
            entry.distance.value / min,
          )
        }
      }
    }

    return {
      nbOrderWithDistanceExtra,
      totalDistanceExtra,
      nbOrderWithDistanceExtraByModulo,
    }
  }, [trips, minDistance, maxDistance])

  // Bag counts
  const bagCounts = useMemo(() => {
    const map = new Map<number, { bagType: any; count: number }>()
    for (const entry of trips) {
      for (const bag of entry.order.bags || []) {
        if (!bag.bagType) continue
        const existing = map.get(bag.bagType.id)
        if (existing) {
          existing.count += bag.number || 1
        } else {
          map.set(bag.bagType.id, {
            bagType: bag.bagType,
            count: bag.number || 1,
          })
        }
      }
    }
    return Array.from(map.values())
  }, [trips])

  const formatTime = (dateStr: string | null | undefined) => {
    if (!dateStr) return ""
    try {
      return new Date(dateStr).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    } catch {
      return ""
    }
  }

  const columns: GridColDef[] = useMemo(() => {
    const cols: GridColDef[] = [
      {
        field: "reference",
        headerName: t("reports.fields.reference"),
        flex: 1,
        minWidth: 120,
      },
      {
        field: "customer",
        headerName: t("reports.fields.customer"),
        flex: 1.5,
        minWidth: 180,
        valueGetter: (_value: any, row: any) => {
          const c = row.client
          return c ? `${c.firstName} ${c.lastName}` : ""
        },
      },
      {
        field: "address",
        headerName: t("reports.fields.address"),
        flex: 2,
        minWidth: 200,
        valueGetter: (_value: any, row: any) => {
          const a = row.address
          return a
            ? `${a.streetNumber} ${a.streetName}, ${a.postcode} ${a.city}`
            : ""
        },
      },
      {
        field: "timeConstraint",
        headerName: t("reports.fields.timeConstraint"),
        width: 130,
        valueGetter: (_value: any, row: any) => {
          const tc = row.timeConstraint
          return tc ? `${tc.start} - ${tc.end}` : ""
        },
      },
      {
        field: "tags",
        headerName: t("reports.fields.tags"),
        width: 200,
        renderCell: (params: any) => (
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {(params.row.tags || []).map((tag: any) => (
              <Chip
                key={tag.id}
                label={tag.label}
                size="small"
                sx={{
                  backgroundColor: tag.color,
                  color: "#fff",
                  fontSize: "0.7rem",
                }}
              />
            ))}
          </Stack>
        ),
      },
      {
        field: "preparedBy",
        headerName: t("reports.fields.preparedBy"),
        width: 130,
      },
      {
        field: "preparationTime",
        headerName: t("reports.fields.preparationTime"),
        width: 120,
      },
      {
        field: "departureTime",
        headerName: t("reports.fields.departureTime"),
        width: 120,
      },
      {
        field: "deliveryTime",
        headerName: t("reports.fields.deliveryTime"),
        width: 120,
      },
      {
        field: "deliveryMessage",
        headerName: t("reports.fields.deliveryMessage"),
        flex: 1,
        minWidth: 150,
      },
      {
        field: "courier",
        headerName: t("reports.fields.courier"),
        width: 150,
      },
      {
        field: "distance",
        headerName: t("reports.fields.distance", {
          unit: parameters.distanceUnit,
        }),
        width: 120,
        align: "right",
        headerAlign: "right",
      },
      {
        field: "interlocutor",
        headerName: t("reports.fields.interlocutor"),
        width: 150,
      },
    ]

    if (report.withSomeCallTime) {
      cols.splice(6, 0, {
        field: "callTime",
        headerName: t("reports.fields.callTime"),
        width: 120,
      })
    }

    if (report.withServiceTime) {
      cols.splice(cols.length - 2, 0, {
        field: "serviceTime",
        headerName: t("reports.fields.serviceTime"),
        width: 120,
        align: "right",
        headerAlign: "right",
      })
    }

    if (parameters.appConfig.orderExternalReferenceEnabled) {
      cols.splice(1, 0, {
        field: "externalReference",
        headerName: t("reports.fields.externalReference"),
        width: 150,
      })
    }

    if (report.providerSize > 1) {
      cols.push({
        field: "providerName",
        headerName: t("reports.fields.provider"),
        width: 150,
      })
    }

    return cols
  }, [t, report, parameters])

  const gridRows = useMemo(
    () =>
      trips.map((e) => ({
        id: e.order.id,
        reference: e.order.reference,
        client: e.order.client,
        address: e.order.address,
        timeConstraint: e.order.timeConstraint,
        tags: e.tags,
        preparedBy: e.creationTraceability?.operatorCode || "",
        callTime: formatTime(e.order.dateCallTime),
        preparationTime: formatTime(e.creationTraceability?.dateTime),
        departureTime: formatTime(e.departureTraceability?.dateTime),
        deliveryTime: formatTime(e.traceability?.dateTime),
        deliveryMessage: e.traceability?.message || "",
        courier: e.courier
          ? `${e.courier.lastName} ${e.courier.firstName}`
          : "",
        distance: e.distance ? e.distance.value.toFixed(1) : "",
        serviceTime:
          e.totalServiceTimeMs > 0
            ? (e.totalServiceTimeMs / 3600000).toFixed(2)
            : "",
        interlocutor: e.traceability?.interlocutor?.identity || "",
        externalReference: e.order.externalReference,
        providerName: e.order.providerName,
      })),
    [trips],
  )

  return (
    <Box
      {...uiSurface("UI-DLV2-193")}
      data-testid="travel-tab"
      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
    >
      {/* Activity Summary */}
      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <Card
          data-testid="travel-trips-card"
          variant="outlined"
          sx={{ minWidth: 140 }}
        >
          <CardContent sx={{ py: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              {t("reports.travel.trips")}
            </Typography>
            <Typography variant="h6">{trips.length}</Typography>
          </CardContent>
        </Card>
        <Card
          data-testid="travel-failed-card"
          variant="outlined"
          sx={{ minWidth: 140 }}
        >
          <CardContent sx={{ py: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              {t("reports.travel.failedAttempts")}
            </Typography>
            <Typography variant="h6">{failedAttempts}</Typography>
          </CardContent>
        </Card>
        <Card
          data-testid="travel-identical-card"
          variant="outlined"
          sx={{ minWidth: 140 }}
        >
          <CardContent sx={{ py: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              {t("reports.travel.identicalAddresses")}
            </Typography>
            <Typography variant="h6">{report.numberOfJoined}</Typography>
          </CardContent>
        </Card>
        <Card
          data-testid="travel-urgent-card"
          variant="outlined"
          sx={{ minWidth: 140 }}
        >
          <CardContent sx={{ py: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              {t("reports.travel.urgentOrders")}
            </Typography>
            <Typography variant="h6">{report.nbEmergencyOrders}</Typography>
          </CardContent>
        </Card>
      </Stack>

      {/* Distance Analysis */}
      <Card data-testid="travel-distance-card" variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            {t("reports.travel.distanceAnalysis")}
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            flexWrap="wrap"
          >
            <TextField
              data-testid="travel-min-distance-input"
              label={t("reports.travel.minDistance")}
              size="small"
              type="number"
              value={minDistance}
              onChange={(e) => setMinDistance(e.target.value)}
              sx={{ width: 120 }}
            />
            <TextField
              data-testid="travel-max-distance-input"
              label={t("reports.travel.maxDistance")}
              size="small"
              type="number"
              value={maxDistance}
              onChange={(e) => setMaxDistance(e.target.value)}
              sx={{ width: 120 }}
            />
            {distanceAnalysis && (
              <>
                <Typography variant="body2">
                  {t("reports.travel.extraOrders")}:{" "}
                  <strong>{distanceAnalysis.nbOrderWithDistanceExtra}</strong>
                </Typography>
                <Typography variant="body2">
                  {t("reports.travel.extraDistance")}:{" "}
                  <strong>
                    {distanceAnalysis.totalDistanceExtra.toFixed(1)}{" "}
                    {parameters.distanceUnit}
                  </strong>
                </Typography>
                <Typography variant="body2">
                  {t("reports.travel.extraByModulo")}:{" "}
                  <strong>
                    {distanceAnalysis.nbOrderWithDistanceExtraByModulo}
                  </strong>
                </Typography>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Toggle and summaries */}
      <Stack direction="row" spacing={2} alignItems="center">
        <FormControlLabel
          control={
            <Checkbox
              checked={showAllTrips}
              onChange={(e) => setShowAllTrips(e.target.checked)}
            />
          }
          label={t("reports.filters.showAllTrips")}
        />
      </Stack>

      {/* Tags Summary */}
      {report.tags.length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              {t("reports.travel.tagsSummary")}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("reports.fields.tag")}</TableCell>
                  <TableCell align="right">
                    {t("reports.fields.count")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.tags.map((tc) => (
                  <TableRow key={tc.tag.id}>
                    <TableCell>{tc.tag.label}</TableCell>
                    <TableCell align="right">{tc.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Bags Summary */}
      {bagCounts.length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              {t("reports.travel.bagsSummary")}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("reports.fields.bagType")}</TableCell>
                  <TableCell align="right">
                    {t("reports.fields.count")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bagCounts.map((bc) => (
                  <TableRow key={bc.bagType.id}>
                    <TableCell>{bc.bagType.name}</TableCell>
                    <TableCell align="right">{bc.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Trips Detail Table */}
      <Typography variant="subtitle1" fontWeight="bold">
        {t("reports.travel.tripsDetail")} ({trips.length})
      </Typography>
      <DataGrid
        data-testid="travel-grid"
        rows={gridRows}
        columns={columns}
        pageSizeOptions={[25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
        autoHeight
        disableRowSelectionOnClick
        density="compact"
      />
    </Box>
  )
}
